import React, { useState, useEffect, useRef } from 'react';
import NumberFormat from 'react-number-format';

/**
 * - A currency component for Brazilian Real
 * - It's based on NumberFormat and accepts all it's props-> https://github.com/s-yadav/react-number-format
 * - It shouldn't be used with `onChange`, but `onValueChange`
 * - `onValueChange` returns three values, the event itself and the the returned object, which has a shape like this:
 *
 ```
 {
  formattedValue: '$23,234,235.56', //value after applying formatting
  value: '23234235.56', //non formatted value as numeric string 23234235.56, if you are setting this value to state make sure to pass isNumericString prop to true
  floatValue: 23234235.56 //floating point representation. For big numbers it can have exponential syntax
}
 */

const CurrencyInput = ({
  maskOptions,
  isCurrencySwitched,
  clearIsCurrencySwitched,
  inputValue,
  onChange,
  ...inputProps
}) => {
  /*
  * The default behavior of the NumberFormat is:
  1. To change the cursor position every time you press the Backspace key
  2. If you type 123 it evaluates to 123,00 instead of 1,23
  3. When you backspace til the field's empty, the final value is not 0,00, but ""
  To change these 3 behaviors the function onValueChange was changed,
  a format function was set and a keydown function was provided
  */
  const [value, setValue] = useState(inputValue);
  const inputRef = useRef(null);
  const positionLastDigit = useRef(null);
  const { prefix, suffix } = maskOptions;

  useEffect(() => {
    if (!inputRef.current) return;

    positionLastDigit.current = inputRef.current.value.length;
    setCaretPosition(inputRef.current, positionLastDigit.current);

    const handleSelectionChange = () => {
      if (!inputRef.current || !prefix) return;

      const selection = document.getSelection();
      if (selection.type === 'None') return;
      const { focusNode: { className: parentClass = '', children = [] } = {} } = selection || {};
      const isDollarInput = Array.from(children).find(el => el.className.includes('dollar') && el.id === 'cc-tip');
      if (parentClass.includes('form-group') && isDollarInput) {
        positionLastDigit.current = inputRef.current.value.length;
        setCaretPosition(inputRef.current, positionLastDigit.current);
      }
    };

    document.removeEventListener('selectionchange', handleSelectionChange);
    document.addEventListener('selectionchange', handleSelectionChange);
  }, [inputRef, prefix]);

  useEffect(() => {
    positionLastDigit.current = inputRef.current.value.length;
  }, [value]);

  const changeInputHandler = () => {
    clearIsCurrencySwitched();
  };

  const changeValueHandler = v => {
    // Set the value to value * 100 because it was divided on the field value prop
    let inputValueComputed = prefix ? inputValue * 10000 : inputValue;

    // catching if currency is switched to update input value
    if (isCurrencySwitched && (value || inputValueComputed) && inputValueComputed !== value) {
      v = {
        formattedValue: prefix ? `${prefix}${inputValueComputed / 100}` : `${inputValueComputed}${suffix}`,
        value: String(prefix ? inputValueComputed / 100 : inputValueComputed),
        floatValue: prefix ? inputValueComputed / 100 : inputValueComputed,
      };
    }

    setValue(prefix ? parseFloat(v.value) * 100 : v.value);
    if (onChange) {
      if (prefix) v = { ...v, value: v.value / 100, floatValue: v.floatValue / 100 };
      onChange({ ...v });
    }
  };

  const currencyFormatter = formatted_value => {
    // for percent only
    // when value is initial '0%'
    // replace '0' by entered digit if pointer is placed before '0' (at the start of the input)
    // so value instead of '10%' would be '1%' (for example when '1' is entered digit)
    if (!prefix && !parseInt(String(value))) {
      const formatted_amount = parseInt(formatted_value);
      formatted_value = `${formatted_amount}`.replace('0', '');
    }

    if (!Number(formatted_value)) return maskOptions.placeholder;

    // catching if currency is switched to update input value
    let inputValueComputed = prefix ? inputValue * 10000 : inputValue;
    if (isCurrencySwitched && (value || inputValueComputed) && inputValueComputed !== value)
      formatted_value = prefix ? inputValueComputed / 100 : inputValueComputed;

    const br = {
      style: prefix ? 'currency' : 'percent',
      ...(prefix && { currency: 'USD' }),
      ...(prefix && { currencyDisplay: 'narrowSymbol' }),
    };

    return new Intl.NumberFormat('pt-BR', br)
      .format(formatted_value / 100)
      .replace(/\s/g, '')
      .replace(/,/g, '.');
  };

  const keyDownHandler = e => {
    if (e.code === 'Backspace' && !value) {
      e.preventDefault();
      return;
    }
    // This if sets the value to 0 and prevent the default for the cursor to keep when there's only cents
    if (prefix && e.code === 'Backspace' && value < 1000) {
      e.preventDefault();
      setValue(0);
    }
  };

  const setCaretPosition = (el, caretPos) => {
    el.value = el.value; // ^ this is used to not only get "focus", but
    // to make sure we don't have it everything -selected-
    // (it causes an issue in chrome, and having it doesn't hurt any other browser)

    if (el !== null) {
      if (el.createTextRange) {
        const range = el.createTextRange();

        range.move('character', caretPos);
        range.select();
        return true;
      } // (el.selectionStart === 0 added for Firefox bug)

      if (el.selectionStart || el.selectionStart === 0) {
        el.focus();
        el.setSelectionRange(caretPos, caretPos);
        return true;
      }

      el.focus();
      return false;
    }
  };

  return (
    <NumberFormat
      {...maskOptions}
      {...inputProps}
      value={prefix ? Number(value) / 100 : Number(value)}
      format={currencyFormatter}
      onValueChange={changeValueHandler}
      getInputRef={inputRef}
      onChange={changeInputHandler}
      onKeyDown={keyDownHandler}
    />
  );
};

export default CurrencyInput;
